/**
 * accountingSpec — Fatture in Cloud + SAP Business One.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/FattureInCloudPage.tsx
 *   ando-frontend/app/pages/configs/fattureInCloudConfig.tsx
 *
 * Note: FiC `schedule_invoice_email` recipient_email gap (memory rule)
 * is HITL-side (ActionFieldEditor), not wizard-side. The wizard only
 * handles the OAuth handshake; the recipient editor lives in the
 * ActionDrawer when an invoice email action lands in the queue.
 */

import type { BaseConnection, CapabilityWizardSpec, FieldSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { FileText, Receipt, Server, Shield, Zap } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface AccountingConnection extends BaseConnection {
  readonly company_id?: string;
  readonly company_name?: string;
  readonly vat_number?: string;
  readonly service_layer_url?: string;
  readonly company_db?: string;
}

const iconCls = "h-4 w-4";

// SAP B1 Service Layer credentials — vendor-tagged step. BE schema
// dispatch (``connectors_unified._credentials_schema_for("sap_b1",
// capability="accounting")``) re-emits this same field set so the
// FE renders BE-canonical fields whenever it's online. Keeping the
// spec mirror lets the wizard preview-render the form before the
// /start call lands.
const sapB1Fields: readonly FieldSpec[] = [
  {
    name: "service_layer_url",
    label: "Service Layer URL",
    type: "url",
    required: true,
    placeholder: "https://b1srv.cliente.com:50000",
    helperText: msg`Endpoint Service Layer della tua installazione SAP B1 (tipicamente porta 50000 HTTPS). Il path /b1s/v2/ viene aggiunto automaticamente.`,
  },
  {
    name: "company_db",
    label: "Company DB",
    type: "string",
    required: true,
    placeholder: "SBODEMO_IT",
    helperText: msg`Nome del database aziendale SAP B1 (selettore società nel client SAP).`,
  },
  {
    name: "username",
    label: msg`Username Service Layer`,
    type: "string",
    required: true,
    placeholder: "manager",
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    required: true,
  },
];

export const accountingSpec: CapabilityWizardSpec<AccountingConnection> = {
  capability: "accounting",
  title: msg`Contabilità`,
  subtitle: msg`Fatture, prima nota, scadenziario — Fatture in Cloud o SAP Business One`,
  icon: <Receipt className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  // Vendor ids MUST match the BE ``_CREDENTIALS_FORM_VENDORS`` /
  // ``_direct_oauth_url_builders`` keys exactly. ``sap_business_one``
  // was a FE-only label leak; the BE registers the driver as ``sap_b1``
  // (June 2026 audit fix).
  vendors: [
    {
      id: "fatture_in_cloud",
      label: "Fatture in Cloud",
      icon: <Receipt className={iconCls} aria-hidden />,
      description: msg`Ideale per PMI italiane — OAuth + API v2`,
    },
    {
      id: "sap_b1",
      label: "SAP Business One",
      icon: <Server className={iconCls} aria-hidden />,
      description: msg`Service Layer OData v4 — mid-market + enterprise`,
    },
  ],

  // Per-vendor steps tagged via ``WizardStepSpec.vendor`` — the wizard
  // picks the right step list after AddAccountMenu's vendor pick. FiC
  // uses managed-OAuth; SAP B1 uses session-cookie credentials form.
  steps: [
    {
      id: "creds_sap_b1",
      kind: "credentials",
      vendor: "sap_b1",
      title: msg`Connessione SAP Business One`,
      description: msg`Inserisci l'endpoint Service Layer + Company DB + credenziali. Ando ottiene un session cookie e si re-loggherà al primo 401.`,
      fields: sapB1Fields,
    },
    {
      id: "authorize_fic",
      kind: "oauth",
      vendor: "fatture_in_cloud",
      title: msg`Autorizza Fatture in Cloud`,
      description: msg`OAuth 2.0 con il tuo account Fatture in Cloud (API v2).`,
      oauth: { buttonLabel: msg`Autorizza` },
    },
  ],

  benefits: [
    {
      icon: <Receipt className={iconCls} aria-hidden />,
      title: msg`Fatturato in linguaggio naturale`,
      body: msg`"Quanto fatturato a Maggio?" — risposta in 2 secondi.`,
    },
    {
      icon: <Zap className={iconCls} aria-hidden />,
      title: msg`Scadenziario proattivo`,
      body: msg`Notifiche su fatture in scadenza e DSO anomali.`,
    },
    {
      icon: <FileText className={iconCls} aria-hidden />,
      title: msg`Documenti collegati`,
      body: msg`Ogni voce è linkata al documento originario, citato in chat.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Compliance GDPR`,
      body: msg`Conservazione e cancellazione conformi al regolamento UE.`,
    },
  ],

  api: createCapabilityApi<AccountingConnection>({ capability: "accounting" }),
  connectionKeyField: "id" as keyof AccountingConnection,
  getDisplayName: (c) =>
    c.company_name ?? c.company_id ?? c.id ?? i18nSingleton._(msg`Azienda`),
  getSecondaryId: (c) => c.vat_number ?? c.company_id ?? null,
};
